// src/stores/auth.js
import { defineStore } from "pinia";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdToken,
} from "firebase/auth";
import { auth } from "@/firebaseConfig";

// This will hold the promise that resolves when auth is ready
let authReadyPromise = null;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    idToken: null,
    loading: false,
    error: null,
    isAuthReady: false,
  }),
  actions: {
    listenToAuthState() {
      if (!authReadyPromise) {
        authReadyPromise = new Promise((resolve) => {
          onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
              this.user = {
                uid: currentUser.uid,
                email: currentUser.email,
              };
              try {
                const token = await getIdToken(currentUser);
                this.idToken = token;
              } catch (e) {
                console.error("Error getting ID token:", e);
                this.user = null;
                this.idToken = null;
              }
            } else {
              this.user = null;
              this.idToken = null;
            }
            this.isAuthReady = true;
            resolve(currentUser);
          });
        });
      }
      return authReadyPromise;
    },

    async signup(email, password) {
      this.loading = true;
      this.error = null;
      try {
        await createUserWithEmailAndPassword(auth, email, password);
      } catch (err) {
        // Using the detailed error handling from your original file
        switch (err.code) {
          case "auth/email-already-in-use":
            this.error =
              "This email address is already registered. Please try logging in or use a different email.";
            break;
          case "auth/invalid-email":
            this.error =
              "The email address you entered is not valid. Please check and try again.";
            break;
          case "auth/weak-password":
            this.error =
              "The password is too weak. It must be at least 6 characters long.";
            break;
          case "auth/operation-not-allowed":
            this.error =
              "Email/password accounts are not enabled. Please contact support.";
            break;
          case "auth/network-request-failed":
            this.error =
              "A network error occurred. Please check your internet connection and try again.";
            break;
          default:
            this.error =
              "An unexpected error occurred during signup. Please try again.";
        }
        this.user = null;
        this.idToken = null;
      } finally {
        this.loading = false;
      }
    },

    async login(email, password) {
      this.loading = true;
      this.error = null;
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err) {
        // Using the detailed error handling from your original file
        switch (err.code) {
          case "auth/invalid-credential":
            this.error =
              "Invalid email or password. Please check your credentials and try again.";
            break;
          case "auth/user-disabled":
            this.error =
              "This account has been disabled. Please contact support.";
            break;
          case "auth/too-many-requests":
            this.error =
              "Access to this account has been temporarily disabled due to many failed login attempts. You can try again later or reset your password.";
            break;
          case "auth/network-request-failed":
            this.error =
              "A network error occurred. Please check your internet connection and try again.";
            break;
          default:
            this.error =
              "An unexpected error occurred during login. Please try again.";
        }
        this.user = null;
        this.idToken = null;
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      this.loading = true;
      this.error = null;
      try {
        await signOut(auth);
      } catch (err) {
        this.error = `${err.message} (Code: ${err.code || "N/A"})`;
      } finally {
        this.loading = false;
      }
    },

    async fetchAndSetIdToken(forceRefresh = true) {
      if (auth.currentUser) {
        try {
          const token = await getIdToken(auth.currentUser, forceRefresh);
          this.idToken = token;
          return this.idToken;
        } catch (e) {
          this.user = null;
          this.idToken = null;
          this.error = `Failed to refresh session (Code: ${
            e.code || "N/A"
          }). Please log in again.`;
          return null;
        }
      }
      return null;
    },
  },
  getters: {
    // Restoring the full list of getters from your original file
    isAuthenticated: (state) => !!state.user,
    currentUser: (state) => state.user,
    currentToken: (state) => state.idToken,
    isLoading: (state) => state.loading,
    authError: (state) => state.error,
  },
});
